import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { parseItemDate } from '../utils/dateUtils';

const DAYS = 14; // 14 days to cover both "this week" (0–7d) and "last week" (7–14d) for trend comparisons

/**
 * usePatternData — fetches 14 days of tracking data for pattern insight computation.
 *
 * Uses one-time getDocs queries (not onSnapshot) — same index-safe pattern as
 * useRecentItems. Queries by childId only, date filtering done client-side.
 *
 * @param {string|null} childId
 * @returns {{ bottle, breastfeeding, sleep, diapers, loading }}
 */
export function usePatternData(childId) {
  const [state, setState] = useState({
    bottle: [], breastfeeding: [], sleep: [], diapers: [], loading: true,
  });

  useEffect(() => {
    if (!childId) {
      setState({ bottle: [], breastfeeding: [], sleep: [], diapers: [], loading: false });
      return;
    }

    let cancelled = false;
    const cutoff = new Date(Date.now() - DAYS * 24 * 60 * 60 * 1000);

    async function fetch() {
      try {
        const collections = ['bottleFeeds', 'breastfeeding', 'sleepTracking', 'diapers'];
        const snaps = await Promise.all(
          collections.map((col) =>
            getDocs(query(collection(db, col), where('childId', '==', childId)))
          )
        );

        const toFiltered = (snap) => {
          const items = [];
          snap.forEach((doc) => items.push({ id: doc.id, ...doc.data() }));
          return items.filter((item) => {
            const d = parseItemDate(item);
            return d && d >= cutoff;
          });
        };

        if (!cancelled) {
          const [bottleSnap, bfSnap, sleepSnap, diaperSnap] = snaps;
          setState({
            bottle:       toFiltered(bottleSnap),
            breastfeeding: toFiltered(bfSnap),
            sleep:        toFiltered(sleepSnap),
            diapers:      toFiltered(diaperSnap),
            loading:      false,
          });
        }
      } catch (err) {
        console.error('usePatternData error:', err);
        if (!cancelled) {
          setState({ bottle: [], breastfeeding: [], sleep: [], diapers: [], loading: false });
        }
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [childId]);

  return state;
}
